import React, { Fragment, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  ActionType,
  ClientIntegrationType,
  GetClientIntegrationDocument,
  GetIntegrationsSummaryDocument,
  GetTicketContextDocument,
  IntegrationType,
  useCreateClientIntegrationMutation,
  useTriggerActionLazyQuery,
  useUpdateClientIntegrationConfigurationMutation,
} from "~/operations";
import { getError } from "~/lib/handle-error";
import { IntegrationAddHeader } from "../../../headers/IntegrationAddHeader";
import { UpdateFlowData } from "../../../utils/integrationsTypes";
import useGenerateIntegrationName from "../../../utils/integrationsUtils";
import { AzureDevOpsFormStepOne } from "./components/AzureDevOpsFormStepOne";
import { AzureDevOpsFormStepTwo } from "./components/AzureDevOpsFormStepTwo";
import { useAzureTicketContext } from "~/hooks/useAzureTicketContext";
import { setDocumentTitle } from "~/lib/commonUtils";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";
import { getIntegrationMrn } from "~/pages/integrations/utils/integrationLinksUtils";

export type AzureDevOpsFormInput = {
  integrationName: string;
  organizationUrl: string;
  tenantId: string;
  servicePrincipalId: string;
  secret: string;
  defaultProject: string;
  autoCloseTickets: boolean;
  autoCreateTickets: boolean;
};

const defaultValues: AzureDevOpsFormInput = {
  integrationName: "",
  autoCloseTickets: false,
  autoCreateTickets: false,
  organizationUrl: "",
  tenantId: "",
  servicePrincipalId: "",
  secret: "",
  defaultProject: "",
};

interface AzureDevOpsIntegrationFormProps {
  scope: SpaceScope | OrganizationScope;
  updateFlow?: UpdateFlowData;
}

export function AzureDevOpsIntegrationForm({
  scope,
  updateFlow,
}: AzureDevOpsIntegrationFormProps) {
  const [searchParams] = useSearchParams();
  const redirectToParam = searchParams.get("redirectTo");
  const stepFromParams = searchParams.get("step");
  const [step, setStep] = useState<number>(Number(stepFromParams) || 1);
  let navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const defaultIntegrationName = useGenerateIntegrationName({ scope });

  const {
    control,
    handleSubmit,
    reset,
    getFieldState,
    formState: { errors, isValid, isSubmitSuccessful },
    watch,
  } = useForm<AzureDevOpsFormInput>({
    mode: "onBlur",
    defaultValues: {
      ...defaultValues,
      integrationName: defaultIntegrationName,
    },
  });

  const { projects, isProjectsEmpty } = useAzureTicketContext({
    scopeMrn: updateFlow?.integration?.mrn || "",
    skip: !updateFlow?.integration.mrn,
  });

  const [watchAutoCreateOnDrift] = watch(["autoCreateTickets"]);

  const [createIntegration] = useCreateClientIntegrationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      const integrationMrn = data.createClientIntegration.integration.mrn;
      triggerClientIntegrationScan({
        variables: { input: { mrn: integrationMrn, type: ActionType.RunScan } },
      });
    },
    refetchQueries: [
      GetTicketContextDocument,
      {
        query: GetIntegrationsSummaryDocument,
        variables: { input: { spaceMrn: scope.mrn } },
      },
      {
        query: GetClientIntegrationDocument,
        variables: {
          mrn: getIntegrationMrn({
            scope,
            integrationId: updateFlow?.integration.mrn.split("/").pop() || "",
          }),
        },
      },
    ],
  });

  const [updateIntegration] = useUpdateClientIntegrationConfigurationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    refetchQueries: [
      {
        query: GetClientIntegrationDocument,
        variables: {
          mrn: getIntegrationMrn({
            scope,
            integrationId: updateFlow?.integration.mrn.split("/").pop() || "",
          }),
        },
      },
    ],
  });

  const [triggerClientIntegrationScan] = useTriggerActionLazyQuery({
    onError(error) {
      console.log("%c Error Scheduling scan on creation", "color: tomato");
      console.log(error.message);
    },
  });

  useEffect(() => {
    if (updateFlow) {
      const configOptions = updateFlow.integration.configurationOptions;
      if (configOptions?.__typename !== "AzureDevopsConfigurationOptions")
        return;

      reset({
        ...defaultValues,
        defaultProject: configOptions.defaultProjectName || "",
        organizationUrl: configOptions.organizationUrl,
        integrationName: updateFlow.integration.name,
        autoCloseTickets: configOptions.autoCloseTickets,
        autoCreateTickets: configOptions.autoCreateTickets,
        tenantId: configOptions.tenantId,
        servicePrincipalId: configOptions.servicePrincipalId,
      });
    }
  }, []);

  useEffect(() => {
    if (isSubmitSuccessful && !updateFlow) {
      reset(defaultValues);
    }
  }, [isSubmitSuccessful]);

  const onSubmit: SubmitHandler<AzureDevOpsFormInput> = async (data) => {
    const azureDevopsConfigurationOptions = {
      organizationUrl: data.organizationUrl,
      tenantId: data.tenantId,
      servicePrincipalId: data.servicePrincipalId,
      autoCloseTickets: data.autoCloseTickets,
      autoCreateTickets: data.autoCreateTickets,
      clientSecret: data.secret,
      defaultProjectName: data.defaultProject,
    };

    const integrationName =
      data.integrationName?.trim() || updateFlow?.integration.name;

    try {
      if (updateFlow || step === 2) {
        await updateIntegration({
          variables: {
            input: {
              name: integrationName,
              mrn: getIntegrationMrn({
                scope,
                integrationId:
                  updateFlow?.integration.mrn.split("/").pop() || "",
              }),
              type: ClientIntegrationType.TicketSystemAzureDevops,
              configurationOptions: {
                azureDevopsConfigurationOptions,
              },
            },
          },
        });
        const integrationId = updateFlow?.integration.mrn.split("/").pop();
        enqueueSnackbar("Successfully updated configuration", {
          variant: "success",
        });
        if (step === 1 && updateFlow) {
          setStep(2);
          return;
        } else {
          navigate(
            `/space/integrations/ticket_system_azure_devops/${integrationId}/?${scope.params}`,
          );
        }
      } else {
        const { data: createIntegrationData } = await createIntegration({
          variables: {
            input: {
              spaceMrn: scope.mrn,
              name: data.integrationName.trim(),
              type: ClientIntegrationType.TicketSystemAzureDevops,
              longLivedToken: false,
              configurationOptions: {
                azureDevopsConfigurationOptions,
              },
            },
          },
        });
        const integrationId =
          createIntegrationData?.createClientIntegration.integration.mrn
            .split("/")
            .pop();
        if (step === 1) {
          navigate(
            `/space/integrations/ticket_system_azure_devops/${integrationId}/edit?${scope.params}&step=2`,
          );
          return;
        }
      }
      if (redirectToParam) {
        navigate(redirectToParam);
      } else {
        navigate(
          `/space/integrations/ticket_system_azure_devops?${scope.params}`,
        );
      }
    } catch (e) {
      const msg = getError(e);
      enqueueSnackbar(msg, { variant: "error" });
    }
  };

  setDocumentTitle(["Azure DevOps", "Integrations Setup"]);

  return (
    <Fragment>
      <IntegrationAddHeader
        {...{ type: IntegrationType.TicketSystemAzureDevops }}
      />
      <Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          {step === 1 && (
            <AzureDevOpsFormStepOne
              control={control}
              isUpdateFlow={!!updateFlow}
              errors={errors}
              getFieldState={getFieldState}
              isValid={isValid}
              disabled={!watchAutoCreateOnDrift}
            />
          )}

          {step === 2 && (
            <AzureDevOpsFormStepTwo
              control={control}
              isValid={isValid}
              isUpdateFlow={!!updateFlow}
              errors={errors}
              projects={projects}
              isProjectsEmpty={isProjectsEmpty}
              disabled={!watchAutoCreateOnDrift}
            />
          )}
        </form>
      </Box>
    </Fragment>
  );
}
